import api from './api';

// Role types based on API response (sys_role table)
export interface Role {
  id: string;
  name?: string;
  roleName?: string;  // Legacy field name
  role_type?: 'Admin' | 'Editor' | 'Viewer' | 'Custom';
  permissions?: string[] | null;
  description?: string;
  remark?: string;    // Legacy field name for description
  status?: number;    // 0=active, 1=system/locked
  tenant_id?: string;
  default_role?: number;
  is_system_default?: number;
  create_time?: string;
  update_time?: string;
  create_by?: string;
  update_by?: string;
  del_flag?: number;
}

export interface RoleListParams {
  search?: string;
  tenantId?: string;
  page?: number;
  pageSize?: number;
}

export interface RoleCreateData {
  roleName: string;
  roleKey?: string;
  roleSort?: number;
  status?: number;
  remark?: string;
}

export interface RoleUpdateData {
  roleName?: string;
  roleKey?: string;
  roleSort?: number;
  status?: number;
  remark?: string;
}

export interface PaginatedRoleResponse {
  items: Role[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

const roleService = {
  /**
   * List all roles (auto-filtered by tenant for Tenant Admins)
   */
  list: async (params?: RoleListParams) => {
    const response = await api.get('/roles', { params });
    return response.data as PaginatedRoleResponse;
  },

  /**
   * Get role by ID (validates ownership)
   */
  getById: async (id: string) => {
    const response = await api.get(`/roles/${id}`);
    return response.data as Role;
  },

  /**
   * Create new role
   */
  create: async (data: RoleCreateData) => {
    const response = await api.post('/roles', data);
    return response.data as Role;
  },

  /**
   * Update role (validates ownership)
   */
  update: async (id: string, data: RoleUpdateData) => {
    const response = await api.put(`/roles/${id}`, data);
    return response.data as Role;
  },

  /**
   * Delete role (validates ownership)
   */
  delete: async (id: string) => {
    await api.delete(`/roles/${id}`);
  },

  /**
   * Get users assigned to a specific role
   */
  getUsersByRole: async (roleId: string, params?: { page?: number; pageSize?: number; search?: string }) => {
    const response = await api.get(`/roles/${roleId}/users`, { params });
    return response.data as {
      items: Array<{
        id: string;
        username: string;
        email: string | null;
        userRealName: string | null;
        status: number;
        assignedAt: string;
        assignedBy: string;
      }>;
      pagination: { page: number; pageSize: number; total: number; totalPages: number };
    };
  },
};

export default roleService;
